import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Bell,
  CopyPlus,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
  X,
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  Boxes,
  Circle,
  CircleDollarSign,
  CircleUser,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coins,
  Copy,
  CreditCard,
  Database,
  FileText,
  Fingerprint,
  Gift,
  Gauge,
  GitFork,
  Globe,
  Info,
  Headphones,
  HeartPulse,
  History,
  LayoutDashboard,
  Layers,
  LayoutList,
  ListChecks,
  Mail,
  MinusCircle,
  Network,
  Package,
  Receipt,
  RefreshCw,
  Search,
  Server,
  Shield,
  ShieldCheck,
  SkipBack,
  SlidersHorizontal,
  Split,
  Store,
  Tag,
  Terminal,
  Ticket,
  Trophy,
  UserCheck,
  UserCog,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';

interface IconProps {
  readonly name: string;
  readonly size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly className?: string;
}

const SIZE_CLASS: Record<NonNullable<IconProps['size']>, string> = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

/**
 * Icon name → lucide component map (epic #835 consolidation). Nav items
 * and page code reference icons by Material Symbols LIGATURE NAMES; the
 * icon font itself is gone, so we translate those names to lucide
 * glyphs here. Unknown names render a neutral circle — never raw
 * ligature text.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  account_circle: CircleUser,
  account_tree: GitFork,
  add: Plus,
  close: X,
  delete: Trash2,
  edit: Pencil,
  library_add: CopyPlus,
  open_in_new: ExternalLink,
  alt_route: Split,
  article: FileText,
  assessment: BarChart3,
  assignment: ClipboardList,
  assignment_ind: UserCheck,
  confirmation_number: Ticket,
  content_copy: Copy,
  conversion_path: Workflow,
  credit_card: CreditCard,
  credit_score: Gauge,
  crisis_alert: AlertTriangle,
  currency_exchange: ArrowLeftRight,
  deployed_code: Boxes,
  dns: Server,
  do_not_disturb_on: MinusCircle,
  error: AlertCircle,
  fact_check: ClipboardCheck,
  fingerprint: Fingerprint,
  first_page: SkipBack,
  groups: Users,
  health_and_safety: HeartPulse,
  history: History,
  arrow_forward: ArrowRight,
  arrow_right: ArrowRight,
  hub: Network,
  info: Info,
  notifications: Bell,
  mail: Mail,
  inventory_2: Package,
  manage_accounts: UserCog,
  manage_search: Search,
  leaderboard: Trophy,
  paid: CircleDollarSign,
  payments: Coins,
  public: Globe,
  query_stats: Activity,
  receipt_long: Receipt,
  redeem: Gift,
  rule: ListChecks,
  rule_settings: SlidersHorizontal,
  refresh: RefreshCw,
  schedule: Clock,
  search: Search,
  sell: Tag,
  shield: Shield,
  shield_person: ShieldCheck,
  space_dashboard: LayoutDashboard,
  storage: Database,
  storefront: Store,
  support_agent: Headphones,
  terminal: Terminal,
  tune: SlidersHorizontal,
  view_carousel: Layers,
  view_list: LayoutList,
  webhook: Webhook,
};

/**
 * Whether a name resolves to a real glyph rather than the neutral fallback.
 *
 * Exists so the nav can be checked against this map by a test instead of by
 * eye. The fallback is deliberately silent at runtime — an unknown name must
 * never render raw ligature text at a user — and silence is exactly what let
 * five nav items ship pointing at names that were never mapped. The map itself
 * stays private; a membership question is all a caller has any business asking.
 */
export function isKnownIconName(name: string): boolean {
  return Object.hasOwn(ICON_MAP, name);
}

export function Icon({ name, size = 'md', className = '' }: IconProps): JSX.Element {
  const Glyph = ICON_MAP[name] ?? Circle;
  return <Glyph aria-hidden="true" className={`${SIZE_CLASS[size]} shrink-0 ${className}`.trim()} />;
}
